import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, CreditCard, Calendar, Percent, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { calculateItemsProfitBatch } from '@/lib/profit-calculation';
import { useLocation } from '@/contexts/LocationContext';

interface DatabaseSale {
  id: string;
  sale_datetime: string;
  total_net: number;
  sale_items: {
    quantity: number;
    cost_each: number;
    line_total_net: number;
    product: {
      name: string;
      current_cost: number;
      last_price_update: string | null;
      item_code: string | null;
    };
  }[];
  sale_payments: {
    payment_type: string;
    amount: number;
  }[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
  }).format(amount);
};

// Calculate profit using Mars prices from same month
const calculateProfitBreakdown = async (
  sales: DatabaseSale[],
  useMarsPrices: boolean = false,
  marsPriceTier: string | null = null
) => {
  // Prepare items for batch calculation
  const items = sales.flatMap(sale =>
    sale.sale_items.map(item => ({
      sku: item.product?.item_code || null,
      productName: item.product?.name || null,
      saleDate: sale.sale_datetime,
      quantity: item.quantity,
      revenue: item.line_total_net,
      cost_each: item.cost_each,
      current_cost: item.product?.current_cost,
    }))
  );

  // Calculate profits (with or without Mars prices)
  // Use same month prices when Mars prices are enabled
  const profitResults = useMarsPrices && marsPriceTier
    ? await calculateItemsProfitBatch(items, marsPriceTier as any, true, true)
    : await calculateItemsProfitBatch(items, null, false, false);

  // Calculate total profit and sales
  let totalProfit = 0;
  let totalSales = 0;
  
  profitResults.forEach(result => {
    totalProfit += result.profit;
  });
  
  sales.forEach(sale => {
    totalSales += sale.total_net;
  });
  
  return {
    totalProfit,
    totalSales,
  };
};

export const DashboardStatistics = () => {
  const { companyId, loading: authLoading } = useAuth();
  const { currentLocation } = useLocation();
  const { settings: profitSettings } = useProfitSettings();

  // Fetch last 5 days sales
  const { data: last5DaysSales = [], isLoading: loading5Days } = useQuery({
    queryKey: ['dashboardStats5Days', companyId, currentLocation?.id],
    queryFn: async (): Promise<DatabaseSale[]> => {
      if (!companyId) return [];
      
      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
      fiveDaysAgo.setHours(0, 0, 0, 0);
      
      // Fetch converted onhold records to identify sales linked to onhold conversions
      let convertedOnHoldsQuery = supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);

      if (currentLocation?.id) {
        convertedOnHoldsQuery = convertedOnHoldsQuery.eq('location_id', currentLocation.id);
      }

      const { data: convertedOnHolds } = await convertedOnHoldsQuery;
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );
      
      let salesQuery = supabase
        .from('sales')
        .select(`
          id,
          sale_datetime,
          total_net,
          sale_items (
            quantity,
            cost_each,
            line_total_net,
            product:products (
              name,
              current_cost,
              last_price_update,
              item_code
            )
          ),
          sale_payments (
            payment_type,
            amount
          )
        `)
        .eq('company_id', companyId)
        .in('status', ['completed', 'open'])
        .gte('sale_datetime', fiveDaysAgo.toISOString());

      if (currentLocation?.id) {
        salesQuery = salesQuery.eq('location_id', currentLocation.id);
      }

      const { data, error } = await salesQuery.order('sale_datetime', { ascending: false });
      
      if (error) {
        console.error('Error fetching last 5 days sales:', error);
        return [];
      }
      
      // Filter out 0 TL sales that are linked to onhold conversions
      return (data || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });
    },
    enabled: !!companyId && !authLoading,
  });

  // Fetch monthly sales
  const { data: monthlySales = [], isLoading: loadingMonthly } = useQuery({
    queryKey: ['dashboardStatsMonthly', companyId, currentLocation?.id],
    queryFn: async (): Promise<DatabaseSale[]> => {
      if (!companyId) return [];
      
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      firstDayOfMonth.setHours(0, 0, 0, 0);
      
      // Fetch converted onhold records to identify sales linked to onhold conversions
      let convertedOnHoldsQuery = supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);

      if (currentLocation?.id) {
        convertedOnHoldsQuery = convertedOnHoldsQuery.eq('location_id', currentLocation.id);
      }

      const { data: convertedOnHolds } = await convertedOnHoldsQuery;
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );
      
      let salesQuery = supabase
        .from('sales')
        .select(`
          id,
          sale_datetime,
          total_net,
          sale_items (
            quantity,
            cost_each,
            line_total_net,
            product:products (
              name,
              current_cost,
              last_price_update,
              item_code
            )
          ),
          sale_payments (
            payment_type,
            amount
          )
        `)
        .eq('company_id', companyId)
        .in('status', ['completed', 'open'])
        .gte('sale_datetime', firstDayOfMonth.toISOString());

      if (currentLocation?.id) {
        salesQuery = salesQuery.eq('location_id', currentLocation.id);
      }

      const { data, error } = await salesQuery.order('sale_datetime', { ascending: false });
      
      if (error) {
        console.error('Error fetching monthly sales:', error);
        return [];
      }
      
      // Filter out 0 TL sales that are linked to onhold conversions
      return (data || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });
    },
    enabled: !!companyId && !authLoading,
  });

  // Calculate last 5 days statistics
  const { data: last5DaysProfitBreakdown } = useQuery({
    queryKey: ['last5DaysProfit', last5DaysSales, profitSettings.enabled, profitSettings.marsPriceTier],
    queryFn: async () => {
      return await calculateProfitBreakdown(
        last5DaysSales,
        profitSettings.enabled,
        profitSettings.enabled ? profitSettings.marsPriceTier : null
      );
    },
    enabled: !!last5DaysSales.length,
  });
  const last5DaysStats = {
    totalSales: last5DaysSales.reduce((sum, sale) => sum + sale.total_net, 0),
    totalProfit: last5DaysProfitBreakdown?.totalProfit || 0,
    profitPercentage: 0,
    paymentTypes: last5DaysSales.reduce((acc, sale) => {
      sale.sale_payments.forEach(payment => {
        const type = payment.payment_type;
        if (!acc[type]) {
          acc[type] = 0;
        }
        acc[type] += payment.amount;
      });
      return acc;
    }, {} as Record<string, number>),
  };
  
  // Calculate profit percentage
  last5DaysStats.profitPercentage = last5DaysStats.totalSales > 0 
    ? (last5DaysStats.totalProfit / last5DaysStats.totalSales) * 100 
    : 0;

  // Calculate monthly statistics
  const { data: monthlyProfitBreakdown } = useQuery({
    queryKey: ['monthlyProfit', monthlySales, profitSettings.enabled, profitSettings.marsPriceTier],
    queryFn: async () => {
      return await calculateProfitBreakdown(
        monthlySales,
        profitSettings.enabled,
        profitSettings.enabled ? profitSettings.marsPriceTier : null
      );
    },
    enabled: !!monthlySales.length,
  });
  const monthlyStats = {
    totalSales: monthlySales.reduce((sum, sale) => sum + sale.total_net, 0),
    totalProfit: monthlyProfitBreakdown?.totalProfit || 0,
    profitPercentage: 0,
    paymentTypes: monthlySales.reduce((acc, sale) => {
      sale.sale_payments.forEach(payment => {
        const type = payment.payment_type;
        if (!acc[type]) {
          acc[type] = 0;
        }
        acc[type] += payment.amount;
      });
      return acc;
    }, {} as Record<string, number>),
  };
  
  // Calculate profit percentage
  monthlyStats.profitPercentage = monthlyStats.totalSales > 0 
    ? (monthlyStats.totalProfit / monthlyStats.totalSales) * 100 
    : 0;

  const getPaymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cash: 'Nakit',
      credit_card: 'Kredi Kartı',
      promissory: 'Senet',
      odero: 'Ödero',
      paro: 'Paro',
      transfer: 'Transfer',
      other: 'Diğer',
    };
    return labels[type] || type;
  };

  if (authLoading || loading5Days || loadingMonthly) {
    return (
      <div className="glass-panel-strong rounded-2xl p-6 md:p-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel-strong rounded-2xl p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold mb-6">İstatistikler</h2>
      
      <div className="space-y-8">
        {/* Last 5 Days Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-accent" />
            <h3 className="text-lg font-semibold">Son 5 Gün</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Satış</p>
                    <p className="text-xl font-bold">{formatCurrency(last5DaysStats.totalSales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Kar</p>
                    <p className="text-xl font-bold text-success">
                      {formatCurrency(last5DaysStats.totalProfit)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Percent className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kar Yüzdesi</p>
                    <p className="text-xl font-bold text-purple-500">
                      {last5DaysStats.profitPercentage.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel md:col-span-2 lg:col-span-1">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-3">Ödeme Yöntemleri</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(last5DaysStats.paymentTypes).map(([type, amount]) => (
                    <div key={type} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2 bg-muted/10 rounded-lg">
                      <span className="text-xs sm:text-sm font-medium truncate">{getPaymentTypeLabel(type)}</span>
                      <span className="text-xs sm:text-sm font-bold whitespace-nowrap">{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  {Object.keys(last5DaysStats.paymentTypes).length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-full">Ödeme verisi yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>

        {/* Monthly Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-accent" />
            <h3 className="text-lg font-semibold">Bu Ay</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Satış</p>
                    <p className="text-xl font-bold">{formatCurrency(monthlyStats.totalSales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Kar</p>
                    <p className="text-xl font-bold text-success">
                      {formatCurrency(monthlyStats.totalProfit)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Percent className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kar Yüzdesi</p>
                    <p className="text-xl font-bold text-purple-500">
                      {monthlyStats.profitPercentage.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel md:col-span-2 lg:col-span-1">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-3">Ödeme Yöntemleri</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(monthlyStats.paymentTypes).map(([type, amount]) => (
                    <div key={type} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2 bg-muted/10 rounded-lg">
                      <span className="text-xs sm:text-sm font-medium truncate">{getPaymentTypeLabel(type)}</span>
                      <span className="text-xs sm:text-sm font-bold whitespace-nowrap">{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  {Object.keys(monthlyStats.paymentTypes).length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-full">Ödeme verisi yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </div>
    </div>
  );
};

